import { Component, DestroyRef, effect, inject, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
    MatTableDataSource,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
} from '@angular/material/table';
import { faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Calendar } from 'src/app/models/centrex/calendar';
import { DaysOfWeek } from 'src/app/models/centrex/enums/days-of-week';
import { CalendarService } from 'src/app/services/centrex/calendar.service';
import { DialogDeleteConfirmationComponent } from '../../dialogs/dialog-delete-confirmation/dialog-delete-confirmation.component';
import { filter, switchMap } from 'rxjs';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { MatInput } from '@angular/material/input';
import { MatFormField, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { MatButton, MatIconButton } from '@angular/material/button';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { DaysPipe } from 'src/app/pipes/days.pipe';
import { InstanceService } from 'src/app/services/centrex/instance.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-calendars',
    templateUrl: './calendars.component.html',
    styleUrls: ['./calendars.component.scss'],
    imports: [
        FaIconComponent,
        MatButton,
        RouterLink,
        MatIcon,
        ReactiveFormsModule,
        MatTable,
        MatSort,
        MatColumnDef,
        MatHeaderCellDef,
        MatHeaderCell,
        MatSortHeader,
        MatFormField,
        MatInput,
        MatIconButton,
        MatSuffix,
        MatTooltip,
        MatCellDef,
        MatCell,
        MatSelect,
        MatOption,
        MatHeaderRowDef,
        MatHeaderRow,
        MatRowDef,
        MatRow,
        MatPaginator,
        TranslateModule,
        DaysPipe,
    ]
})
export class CalendarsComponent {
    private readonly instanceService = inject(InstanceService);
    private destroyRef = inject(DestroyRef);

    // Icône
    public faAngleRight = faAngleRight;

    displayedColumns: string[] = ['name', 'days', 'actions'];
    dataSource: MatTableDataSource<Calendar>;
    deleteConfirmationDialog: MatDialogRef<DialogDeleteConfirmationComponent>;

    daysOfWeek = Object.keys(DaysOfWeek);

    filtersForm: UntypedFormGroup;

    @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;
    @ViewChild(MatSort, { static: false }) sort: MatSort;

    constructor(
        private calendarService: CalendarService,
        private dialog: MatDialog,
        private translate: TranslateService,
        private toastr: ToastrService,
        private formbuilder: UntypedFormBuilder,
        private spinner: NgxSpinnerService
    ) {
        this.filtersForm = new UntypedFormGroup({});

        this.initFiltersForm();

        effect(() => {
            if (this.instanceService.instance()) {
                this.loadDatas();
                this.initFiltersForm();
            }
        });
    }

    /**
     * Initialisation du formulaire des filtres du tableau des calendriers
     */
    initFiltersForm() {
        this.filtersForm = this.formbuilder.group({
            id: [''],
            name: [''],
            days: [''],
        });
    }

    loadDatas() {
        this.spinner.show();
        this.calendarService
            .getCalendars()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (calendars) => {
                    this.dataSource = new MatTableDataSource(calendars);

                    this.dataSource.paginator = this.paginator;
                    this.dataSource.sort = this.sort;

                    this.createFilters();

                    this.spinner.hide();
                },
                error: () => {
                    this.spinner.hide();
                },
            });
    }

    createFilters() {
        this.dataSource.filterPredicate = (data: Calendar, filter: string) => {
            const filterArray = filter.split('¤');
            const matchFilter = [];

            matchFilter.push(data.id && data.id.trim().toLowerCase().includes(filterArray[0].trim().toLowerCase()));
            matchFilter.push(data.name && data.name.trim().toLowerCase().includes(filterArray[1].trim().toLowerCase()));

            const daysOfWeekFilters = filterArray[2] ? filterArray[2].split(',').map((a) => this.translate.instant('EnumDaysOfWeek.' + a)) : null;

            let days: string[] = [];
            data.days.split(',').forEach((day) => {
                days.push(this.translate.instant('EnumDaysOfWeek.' + day.trim()));
            });

            let containDay: boolean = false;
            if (daysOfWeekFilters && days.length > 0) {
                days.forEach((a) => {
                    if (daysOfWeekFilters.includes(a)) {
                        containDay = true;
                    }
                });

                matchFilter.push(data.days && containDay);
            } else {
                matchFilter.push(data.days);
            }

            return matchFilter.every(Boolean);
        };

        this.displayedColumns = ['name', 'days', 'actions'];

        this.applyFilters();
    }

    /**
     * Application du filtre dans le tableau des calendriers
     */
    applyFilters() {
        const id = this.filtersForm.get('id').value?.toLowerCase();
        const name = this.filtersForm.get('name').value?.toLowerCase();
        const days = this.filtersForm.get('days').value;

        const filterValue = (!!id ? id : '') + '¤' + (!!name ? name : '') + '¤' + (!!days ? days : '');

        this.dataSource.filter = filterValue.trim();

        if (this.dataSource.paginator) {
            this.dataSource.paginator.firstPage();
        }
    }

    /**
     * Réinitialise le filtre de la colonne spécifiée.
     * @param formControlName Nom du contrôle du formulaire
     */
    resetColumnFilter(formControlName: string): void {
        this.filtersForm.controls[formControlName].reset();
        this.applyFilters();
    }

    /**
     * Réinitialise tous les filtres de colonne
     */
    resetAllColumnFilter() {
        this.filtersForm.reset();
        this.applyFilters();
    }

    onDeleteClick(calendarId: string, calendarName: string) {
        this.deleteConfirmationDialog = this.dialog.open(DialogDeleteConfirmationComponent, {
            panelClass: 'default-dialog',
            disableClose: true,
        });
        this.deleteConfirmationDialog.componentInstance.confirmMessage = this.translate.instant('DialogDeleteConfirmation.CalendarConfirmMessage', { calendarName: calendarName });

        this.deleteConfirmationDialog
            .afterClosed()
            .pipe(
                filter((result) => result === true),
                switchMap(() => this.calendarService.deleteCalendar(calendarId)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: (x) => {
                    this.toastr.success(
                        this.translate.instant('Calendars.SuccessMessageDeleteCalendar', {
                            calendarName: calendarName,
                        })
                    );

                    this.loadDatas();
                },
                error: () => {
                    this.toastr.error(
                        this.translate.instant('Calendars.ErrorMessageDeleteCalendar', {
                            calendarName: calendarName,
                        })
                    );
                },
                complete: () => {
                    this.deleteConfirmationDialog = null;
                },
            });
    }
}
